import { useState } from "react";
import { searchDocumentsByContentAction } from "../actions/document.actions";

export enum DocumentLocatorStatus {
  Idle = "idle",
  Loading = "loading",
  Success = "success",
  NoResults = "no-results",
  Error = "error",
}

type Document = {
  id: string;
  secureUrl: string;
  width: number;
  height: number;
  type: string;
};

type DocumentLocatorConfig = {
  projectId?: string;
  afterLocate?: (results: Document[] | null) => void;
};

const useDocumentLocator = ({
  projectId,
  afterLocate,
}: DocumentLocatorConfig) => {
  const [status, setStatus] = useState<DocumentLocatorStatus>(
    DocumentLocatorStatus.Idle
  );

  const locateDocuments = async (
    searches: {
      queryTerms: string[];
      documentType?: string;
      excludeOrgDocuments?: boolean;
    }[]
  ) => {
    setStatus(DocumentLocatorStatus.Loading);

    try {
      const sanitizedSearches = searches
        .map((search) => ({
          ...search,
          queryTerms: search.queryTerms
            .map((term) => term?.trim())
            .filter(Boolean),
        }))
        .filter((search) => search.queryTerms.length);

      if (!sanitizedSearches.length) {
        setStatus(DocumentLocatorStatus.NoResults);
        return;
      }

      const results = await Promise.all(
        sanitizedSearches.map(
          ({ queryTerms, documentType, excludeOrgDocuments }) =>
            searchDocumentsByContentAction({
              projectId,
              queryTerms,
              documentType,
              excludeOrgDocuments,
            })
        )
      );

      const docs: Document[] = [];
      let hadError = false;

      for (const [docsError, found] of results) {
        if (docsError) {
          console.error(docsError.message);
          hadError = true;
        } else if (found?.length) {
          docs.push(...found);
        }
      }

      const uniqueDocs = docs.filter(
        (doc, index) =>
          docs.findIndex((d) => d.id === doc.id && d.type === doc.type) ===
          index
      );

      if (uniqueDocs.length) {
        setStatus(DocumentLocatorStatus.Success);
        afterLocate?.(uniqueDocs);
      } else {
        setStatus(
          hadError ? DocumentLocatorStatus.Error : DocumentLocatorStatus.NoResults
        );
      }
    } catch (err) {
      console.error(err);
      setStatus(DocumentLocatorStatus.Error);
    }
  };

  const locateDocument = async (opts: {
    queryTerms: string[];
    documentType?: string;
    excludeOrgDocuments?: boolean;
  }) => {
    await locateDocuments([opts]);
  };

  return {
    docLocateStatus: status,
    locateDocument,
    locateDocuments,
  };
};

export default useDocumentLocator;
